import { CapacitorNotificationProvider } from './CapacitorNotificationProvider';
import { CapacitorPushNotificationProvider } from './CapacitorPushNotificationProvider';
import { getPlatform } from './utils';
export class NotificationManager {
    static instance;
    isSupported = false;
    permission = 'default';
    scheduledNotifications = new Map();
    capacitorProvider = null;
    pushProvider = null;
    platform = 'web';
    analytics;
    pushService;
    currentUserId;
    deviceToken;
    constructor() {
        this.platform = getPlatform();
        this.initializeProviders();
    }
    async initializeProviders() {
        this.checkSupport();
        if (this.platform === 'capacitor') {
            // Initialize push notifications first (priority)
            this.pushProvider = new CapacitorPushNotificationProvider(this.analytics);
            const pushInitialized = await this.pushProvider.initialize();
            if (pushInitialized) {
                this.deviceToken = this.pushProvider.getRegistrationToken();
                console.log('✅ Push notifications initialized successfully');
            }
            // Keep local notifications as fallback only
            this.capacitorProvider = new CapacitorNotificationProvider(this.analytics);
            this.capacitorProvider.setupNotificationListeners();
        }
    }
    setAnalytics(analytics) {
        this.analytics = analytics;
        // Re-initialize capacitor providers with analytics if they exist
        if (this.platform === 'capacitor') {
            if (this.pushProvider) {
                this.pushProvider = new CapacitorPushNotificationProvider(analytics);
                this.pushProvider.initialize();
            }
            if (this.capacitorProvider) {
                this.capacitorProvider = new CapacitorNotificationProvider(analytics);
                this.capacitorProvider.setupNotificationListeners();
            }
        }
    }
    setPushService(pushService) {
        this.pushService = pushService;
    }
    setCurrentUser(userId) {
        this.currentUserId = userId;
        // Update device token if we have push service and device token
        if (this.pushService && this.deviceToken && userId) {
            this.pushService.updateDeviceToken(userId, this.deviceToken).catch(error => {
                console.error('Failed to update device token:', error);
            });
        }
        // Also try to get FCM token if available
        this.updateFCMToken(userId);
    }
    async updateFCMToken(userId) {
        // This will be handled by the main app's notification provider
        // The FCM token will be set via setDeviceToken method
        console.log('ℹ️ FCM token update requested for user:', userId);
    }
    setDeviceToken(token) {
        this.deviceToken = token;
        console.log('✅ Device token set:', token ? token.substring(0, 20) + '...' : 'None');
    }
    getDeviceToken() {
        return this.deviceToken || null;
    }
    static getInstance() {
        if (!NotificationManager.instance) {
            NotificationManager.instance = new NotificationManager();
        }
        return NotificationManager.instance;
    }
    checkSupport() {
        this.isSupported = 'Notification' in window && 'serviceWorker' in navigator;
        if (this.isSupported) {
            this.refreshPermissionStatus();
        }
    }
    refreshPermissionStatus() {
        // Always get fresh permission status from the browser
        if ('Notification' in window) {
            this.permission = Notification.permission;
        }
    }
    async requestPermission() {
        if (!this.isSupported) {
            console.warn('Notifications are not supported in this browser');
            return 'denied';
        }
        // Refresh current permission status before checking
        this.refreshPermissionStatus();
        if (this.permission === 'granted') {
            return 'granted';
        }
        try {
            this.permission = await Notification.requestPermission();
            return this.permission;
        }
        catch (error) {
            console.error('Error requesting notification permission:', error);
            return 'denied';
        }
    }
    async sendNotification(data, delay = 0) {
        // Track notification scheduling
        if (this.analytics && delay > 0) {
            await this.analytics.trackEvent('push_notification_scheduled', {
                notification_type: data.data?.type || 'unknown',
                delay_seconds: delay,
                platform: this.platform,
                uses_push_service: Boolean(this.pushService),
            });
        }
        // Priority 1: Use Push Service if available and we have user context OR guest device token
        if (this.pushService && this.deviceToken && delay > 0) {
            try {
                const scheduledFor = new Date(Date.now() + delay * 1000).toISOString();
                if (this.currentUserId) {
                    // Authenticated user
                    await this.pushService.scheduleNotification({
                        userId: this.currentUserId,
                        deviceToken: this.deviceToken,
                        title: data.title,
                        body: data.body,
                        data: data.data ? Object.fromEntries(Object.entries(data.data).map(([key, value]) => [key, String(value)])) : {},
                        scheduledFor,
                        challengeId: data.data?.challengeId || 'unknown',
                    });
                }
                else {
                    // Guest user - use guest notification service
                    await this.pushService.scheduleGuestNotification({
                        deviceToken: this.deviceToken,
                        title: data.title,
                        body: data.body,
                        data: data.data ? Object.fromEntries(Object.entries(data.data).map(([key, value]) => [key, String(value)])) : {},
                        scheduledFor,
                        challengeId: data.data?.challengeId || 'unknown',
                    });
                }
                console.log('✅ Notification scheduled via push service');
                return;
            }
            catch (error) {
                console.error('❌ Failed to schedule via push service, falling back:', error);
                // Fall through to next option
            }
        }
        else {
            console.log('⚠️ Push service not available or missing device token, using fallback');
        }
        // Priority 2: Use Capacitor Push Notifications for immediate notifications
        if (this.platform === 'capacitor' &&
            this.pushProvider?.isAvailable() &&
            delay === 0) {
            // For immediate notifications, show in-app notification since user is in the app
            this.showInAppNotification(data);
            return;
        }
        // Priority 3: Use Capacitor Local Notifications as fallback
        if (this.platform === 'capacitor' &&
            this.capacitorProvider?.isAvailable()) {
            console.log('🔄 Using local notifications as fallback');
            await this.capacitorProvider.scheduleNotification(data, delay);
            return;
        }
        // Priority 4: Web notifications fallback
        if (delay === 0) {
            // Send immediately - show in-app notification since user is in the app
            this.showInAppNotification(data);
            return;
        }
        // Schedule notification for later (when user leaves the app)
        this.scheduleNotification(data, delay);
    }
    showInAppNotification(data) {
        // Dispatch custom event for in-app notification
        const event = new CustomEvent('show-in-app-notification', {
            detail: {
                title: data.title,
                body: data.body,
                type: 'info',
            },
        });
        window.dispatchEvent(event);
    }
    scheduleNotification(data, delay) {
        const notificationId = `notification-${Date.now()}-${Math.random()}`;
        // Schedule the notification
        const timeoutId = window.setTimeout(() => {
            this.sendSystemNotification(data);
            this.scheduledNotifications.delete(notificationId);
        }, delay * 1000);
        this.scheduledNotifications.set(notificationId, timeoutId);
        // Set up visibility change listener to trigger notifications when user leaves
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // User left the app, notification will be sent after delay
                console.log(`Notification scheduled to be sent in ${delay} seconds`);
            }
            else {
                // User returned to app, cancel pending notifications
                this.clearScheduledNotification(notificationId);
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    async sendSystemNotification(data) {
        if (!this.isSupported) {
            console.warn('System notifications not supported');
            return;
        }
        const permission = await this.requestPermission();
        if (permission !== 'granted') {
            console.warn('Notification permission not granted');
            return;
        }
        try {
            const notification = new Notification(data.title, {
                body: data.body,
                icon: data.icon,
                badge: data.badge,
                data: data.data,
                silent: data.silent,
                requireInteraction: data.requireInteraction,
                tag: data.tag,
            });
            // Handle notification click
            notification.onclick = async (event) => {
                event.preventDefault();
                window.focus();
                notification.close();
                // Track notification open
                if (this.analytics) {
                    await this.analytics.trackEvent('local_notification_open', {
                        notification_type: data.data?.type || 'unknown',
                        platform: 'web',
                    });
                }
                // Dispatch custom event for notification click
                const clickEvent = new CustomEvent('notification-click', {
                    detail: data,
                });
                window.dispatchEvent(clickEvent);
            };
        }
        catch (error) {
            console.error('Error sending system notification:', error);
        }
    }
    clearScheduledNotification(notificationId) {
        const timeoutId = this.scheduledNotifications.get(notificationId);
        if (timeoutId) {
            clearTimeout(timeoutId);
            this.scheduledNotifications.delete(notificationId);
        }
    }
    async clearAllScheduledNotifications() {
        // Clear push service notifications if available
        if (this.pushService && this.currentUserId) {
            try {
                await this.pushService.cancelAllUserNotifications(this.currentUserId);
                console.log('✅ Cleared push service notifications');
            }
            catch (error) {
                console.error('❌ Failed to clear push service notifications:', error);
            }
        }
        // Clear web notifications
        this.scheduledNotifications.forEach(timeoutId => {
            clearTimeout(timeoutId);
        });
        this.scheduledNotifications.clear();
        // Clear Capacitor notifications if available
        if (this.platform === 'capacitor' &&
            this.capacitorProvider?.isAvailable()) {
            await this.capacitorProvider.cancelAll();
        }
    }
    async clearNotificationsForChallenge(challengeId) {
        // Clear push service notifications for specific challenge
        if (this.pushService && this.currentUserId) {
            try {
                await this.pushService.cancelNotifications(this.currentUserId, challengeId);
                console.log(`✅ Cleared push notifications for challenge: ${challengeId}`);
            }
            catch (error) {
                console.error(`❌ Failed to clear push notifications for challenge ${challengeId}:`, error);
            }
        }
        // Note: For web and local notifications, we don't have challenge-specific cancellation
        // This would require implementing a more sophisticated tracking system
    }
    getPermissionStatus() {
        // Always return fresh permission status
        this.refreshPermissionStatus();
        return this.permission;
    }
    isNotificationSupported() {
        return (this.isSupported ||
            (this.platform === 'capacitor' &&
                Boolean(this.pushProvider?.isAvailable())) ||
            Boolean(this.pushService));
    }
    getPlatform() {
        return this.platform;
    }
    async getPendingNotifications() {
        if (this.platform === 'capacitor' &&
            this.capacitorProvider?.isAvailable()) {
            return await this.capacitorProvider.getPending();
        }
        // For web, we can only return scheduled notifications count
        return Array.from(this.scheduledNotifications.keys()).map(id => ({ id }));
    }
    isCapacitorAvailable() {
        return (this.platform === 'capacitor' &&
            Boolean(this.capacitorProvider?.isAvailable()));
    }
    isPushServiceAvailable() {
        return Boolean(this.pushService && this.currentUserId && this.deviceToken);
    }
    // Push notification methods
    async initializePushNotifications() {
        if (this.pushProvider) {
            const initialized = await this.pushProvider.initialize();
            if (initialized) {
                this.deviceToken = this.pushProvider.getRegistrationToken();
                // Update device token if we have user context
                if (this.pushService && this.currentUserId && this.deviceToken) {
                    await this.pushService.updateDeviceToken(this.currentUserId, this.deviceToken);
                }
            }
            return initialized;
        }
        return false;
    }
    getPushNotificationToken() {
        return this.deviceToken || this.pushProvider?.getRegistrationToken() || null;
    }
    async getDeliveredPushNotifications() {
        if (this.pushProvider) {
            return await this.pushProvider.getDeliveredNotifications();
        }
        return [];
    }
    async removeAllDeliveredPushNotifications() {
        if (this.pushProvider) {
            await this.pushProvider.removeAllDeliveredNotifications();
        }
    }
    isPushNotificationAvailable() {
        return Boolean(this.pushProvider?.isAvailable());
    }
}
// Create and export singleton instance
export const notificationManager = NotificationManager.getInstance();
